import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';

export interface BatchConfigOptions {
  applyBankAmount: boolean;
  bankAmountValue: number;
  applyFees: boolean;
  selectedFees: any[];
  applyAdjustmentReason: boolean;
  adjustmentReasonValue: string;
  applyNotes: boolean;
  notesValue: string;
}

@Component({
  selector: 'app-batch-config-modal',
  templateUrl: './batch-config-modal.component.html',
  styleUrl: './batch-config-modal.component.scss'
})
export class BatchConfigModalComponent implements OnInit {

  // === INPUTS ===
  @Input() isVisible: boolean = false;
  @Input() selectedReceipts: any[] = [];
  @Input() feeTypes: any[] = [];

  // === OUTPUTS ===
  @Output() close = new EventEmitter<void>();
  @Output() apply = new EventEmitter<BatchConfigOptions>();

  // === FORM ===
  batchConfigForm!: FormGroup;

  // === PROPIEDADES ===
  activeTab: 'amounts' | 'fees' | 'notes' = 'amounts';

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    this.batchConfigForm = this.fb.group({
      // Configuración de montos
      applyBankAmount: [false],
      bankAmountValue: [0, [Validators.min(0.01)]],
      bankAmountMode: ['fixed'], // 'fixed', 'percentage', 'original'
      bankAmountPercentage: [100, [Validators.min(1), Validators.max(200)]],
      
      // Configuración de fees
      applyFees: [false],
      selectedFees: [[]],
      
      // Configuración de razón de ajuste
      applyAdjustmentReason: [false],
      adjustmentReasonValue: [''],
      
      // Configuración de notas
      applyNotes: [false],
      notesValue: [''],
      notesMode: ['replace'] // 'replace', 'append'
    });
  }

  // === NAVEGACIÓN ENTRE PESTAÑAS ===
  
  setActiveTab(tab: 'amounts' | 'fees' | 'notes'): void {
    this.activeTab = tab;
  }

  // === GETTERS ===

  get totalSelectedReceipts(): number {
    return this.selectedReceipts.length;
  }

  get totalOriginalAmount(): number {
    return this.selectedReceipts.reduce((total, receipt) => {
      const amount = receipt.type === 'income' ? receipt.netAmount : receipt.totalToPay;
      return total + amount;
    }, 0);
  }

  get estimatedBankAmount(): number {
    const mode = this.batchConfigForm.get('bankAmountMode')?.value;
    const fixedValue = this.batchConfigForm.get('bankAmountValue')?.value || 0;
    const percentage = this.batchConfigForm.get('bankAmountPercentage')?.value || 100;

    switch (mode) {
      case 'fixed':
        return fixedValue * this.totalSelectedReceipts;
      case 'percentage':
        return (this.totalOriginalAmount * percentage) / 100;
      case 'original':
        return this.totalOriginalAmount;
      default:
        return 0;
    }
  }

  get selectedFeesForBatch(): any[] {
    return this.batchConfigForm.get('selectedFees')?.value || [];
  }

  // === GESTIÓN DE FEES PARA LOTES ===

  addFeeForBatch(): void {
    const newFee = {
      feeTypeId: '',
      requestAmount: '',
      description: 'Aplicado por lotes'
    };
    
    const currentFees = this.selectedFeesForBatch;
    currentFees.push(newFee);
    this.batchConfigForm.patchValue({ selectedFees: currentFees });
  }

  removeFeeForBatch(index: number): void {
    const currentFees = this.selectedFeesForBatch;
    currentFees.splice(index, 1);
    this.batchConfigForm.patchValue({ selectedFees: currentFees });
  }

  updateFeeForBatch(index: number, field: string, value: any): void {
    const currentFees = this.selectedFeesForBatch;
    if (currentFees[index]) {
      currentFees[index][field] = value;
      this.batchConfigForm.patchValue({ selectedFees: currentFees });
    }
  }

  // === MÉTODOS AUXILIARES PARA EVENT HANDLERS ===

  onFeeTypeChange(index: number, event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.updateFeeForBatch(index, 'feeTypeId', target.value);
  }

  onFeeAmountChange(index: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    this.updateFeeForBatch(index, 'requestAmount', target.value);
  }

  onFeeDescriptionChange(index: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    this.updateFeeForBatch(index, 'description', target.value);
  }

  // === VALIDACIONES ===

  isFormValid(): boolean {
    const form = this.batchConfigForm;
    
    // Al menos una opción debe estar seleccionada
    const hasAnyOption = form.get('applyBankAmount')?.value ||
                        form.get('applyFees')?.value ||
                        form.get('applyAdjustmentReason')?.value ||
                        form.get('applyNotes')?.value;
    
    if (!hasAnyOption) return false;

    // Validar monto del banco si está seleccionado
    if (form.get('applyBankAmount')?.value) {
      const mode = form.get('bankAmountMode')?.value;
      if (mode === 'fixed') {
        const value = form.get('bankAmountValue')?.value;
        if (!value || value <= 0) return false;
      }
    }

    // Validar fees si están seleccionados
    if (form.get('applyFees')?.value) {
      const fees = this.selectedFeesForBatch;
      if (fees.length === 0) return false;
      
      // Validar que todos los fees tengan datos válidos
      for (const fee of fees) {
        if (!fee.feeTypeId || !fee.requestAmount) return false;
      }
    }

    // Validar razón de ajuste si está seleccionada
    if (form.get('applyAdjustmentReason')?.value) {
      const reason = form.get('adjustmentReasonValue')?.value;
      if (!reason || reason.trim().length === 0) return false;
    }

    // Validar notas si están seleccionadas
    if (form.get('applyNotes')?.value) {
      const notes = form.get('notesValue')?.value;
      if (!notes || notes.trim().length === 0) return false;
    }

    return true;
  }

  // === ACCIONES ===

  onApply(): void {
    if (!this.isFormValid()) return;

    const formValue = this.batchConfigForm.value;
    const config: BatchConfigOptions = {
      applyBankAmount: formValue.applyBankAmount,
      bankAmountValue: this.calculateBankAmountForReceipt(),
      applyFees: formValue.applyFees,
      selectedFees: formValue.selectedFees,
      applyAdjustmentReason: formValue.applyAdjustmentReason,
      adjustmentReasonValue: formValue.adjustmentReasonValue,
      applyNotes: formValue.applyNotes,
      notesValue: formValue.notesValue
    };

    this.apply.emit(config);
  }

  private calculateBankAmountForReceipt(): number {
    const mode = this.batchConfigForm.get('bankAmountMode')?.value;
    const fixedValue = this.batchConfigForm.get('bankAmountValue')?.value || 0;
    const percentage = this.batchConfigForm.get('bankAmountPercentage')?.value || 100;

    switch (mode) {
      case 'fixed':
        return fixedValue;
      case 'percentage':
        return percentage / 100; // Se aplicará por comprobante individual
      case 'original':
        return 1; // Se usará el monto original
      default:
        return 0;
    }
  }

  onClose(): void {
    this.close.emit();
  }

  onReset(): void {
    this.initializeForm();
  }

  // === UTILIDADES ===

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  getReceiptTypeIcon(receipt: any): string {
    return receipt.type === 'income' ? '💰' : '💸';
  }

  getReceiptTypeName(receipt: any): string {
    return receipt.type === 'income' ? 'Ingreso' : 'Egreso';
  }

  // === PREVIEW DE APLICACIÓN ===

  getPreviewData(): any[] {
    if (!this.batchConfigForm.get('applyBankAmount')?.value) return [];

    return this.selectedReceipts.slice(0, 3).map(receipt => {
      const originalAmount = receipt.type === 'income' ? receipt.netAmount : receipt.totalToPay;
      let bankAmount = 0;

      const mode = this.batchConfigForm.get('bankAmountMode')?.value;
      const fixedValue = this.batchConfigForm.get('bankAmountValue')?.value || 0;
      const percentage = this.batchConfigForm.get('bankAmountPercentage')?.value || 100;

      switch (mode) {
        case 'fixed':
          bankAmount = fixedValue;
          break;
        case 'percentage':
          bankAmount = (originalAmount * percentage) / 100;
          break;
        case 'original':
          bankAmount = originalAmount;
          break;
      }

      return {
        ...receipt,
        originalAmount,
        calculatedBankAmount: bankAmount,
        difference: bankAmount - originalAmount
      };
    });
  }
}
